import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Rocket,
  TrendingUp,
  Zap,
  Building2,
  Target,
  ShieldCheck,
  Users,
  Star,
} from "lucide-react";

const MILESTONES = [
  {
    year: "2023",
    title: "A Vision",
    icon: Rocket,
    body: "Neuravolt was founded with the vision of building intelligent software and AI powered digital products for startups and businesses.",
    color: "#FF5A1F",
  },
  {
    year: "2024",
    title: "Growth",
    icon: TrendingUp,
    body: "Expanded into web development, mobile applications, AI, cloud infrastructure, and delivered solutions across multiple industries.",
    color: "#FF5A1F",
  },
  {
    year: "2025",
    title: "Momentum",
    icon: Zap,
    body: "Scaled into a full-service software engineering company with ERP, HRMS, CRM, IoT, enterprise software and automation solutions.",
    color: "#FF5A1F",
  },
  {
    year: "2026",
    title: "The Evolution",
    icon: Building2,
    body: "Officially incorporated as VOLTNOVATE SOLUTIONS LLP and recognized as a Startup India (DPIIT) startup, positioning Neuravolt as an AI first software engineering company serving clients globally.",
    color: "#FF5A1F",
  },
];

const PILLARS = [
  {
    icon: Target,
    title: "Purpose Driven",
    desc: "Solving real problems",
  },
  {
    icon: ShieldCheck,
    title: "Innovation First",
    desc: "Tech that creates impact",
  },
  {
    icon: Users,
    title: "Client Focused",
    desc: "Partnerships that grow",
  },
  {
    icon: Star,
    title: "Future Ready",
    desc: "Always evolving",
  },
];

// Desktop Positions (horizontal percentage)
const POSITIONS = [22, 44, 66, 88];
// Delay for each dot to light up (seconds), slightly after its position along the fill
const DOT_DELAYS = [0.4, 0.8, 1.2, 1.6];


export const Timeline = () => {
  const trackRef = useRef(null);
  const lineRef = useRef(null);
  const isInView = useInView(lineRef, { once: true, margin: "-10% 0px" });
  const [hoveredIdx, setHoveredIdx] = useState(null);

  return (
    <section
      data-testid="timeline"
      className="relative bg-[#07080a] text-white py-24 px-4 sm:px-6 lg:px-8 overflow-hidden select-none"
    >


      {/* Background Ambient Glow & Waves */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Subtle orange aura top left & bottom right */}
        <div className="absolute -top-32 left-1/4 w-[600px] h-[600px] bg-[#FF5A1F]/10 blur-[140px] rounded-full" />
        <div className="absolute -bottom-32 right-1/4 w-[500px] h-[500px] bg-[#FF7700]/10 blur-[130px] rounded-full" />

        {/* Flowing background lines */}
        <svg className="absolute inset-0 w-full h-full opacity-15" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M -100 200 C 300 50, 700 350, 1600 150"
            fill="none"
            stroke="url(#orange-grad)"
            strokeWidth="1.5"
          />
          <path
            d="M -100 350 C 400 500, 900 100, 1600 400"
            fill="none"
            stroke="url(#orange-grad)"
            strokeWidth="1"
          />
          <defs>
            <linearGradient id="orange-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FF5A1F" stopOpacity="0" />
              <stop offset="50%" stopColor="#FF5A1F" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#FF5A1F" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* Large Translucent Background Typography Watermarks */}
        <span className="absolute left-2 sm:left-10 top-1/2 -translate-y-1/2 font-display text-[7rem] sm:text-[11rem] md:text-[14rem] font-extrabold text-[#13151b]/40 italic tracking-tighter">
          2023
        </span>
        <span className="absolute right-2 sm:right-10 top-1/2 -translate-y-1/2 font-display text-[7rem] sm:text-[11rem] md:text-[14rem] font-extrabold text-[#13151b]/40 italic tracking-tighter">
          2026
        </span>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="text-center mb-16 sm:mb-20">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center justify-center gap-3 text-[#FF5A1F] font-heading text-xs font-semibold uppercase tracking-[0.35em] mb-4"
          >
            <span className="w-7 h-[1.5px] bg-[#FF5A1F]" />
            OUR JOURNEY
            <span className="w-7 h-[1.5px] bg-[#FF5A1F]" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white"
          >
            The Neuravolt{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5A1F] to-[#FF7700]">
              Evolution
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-neutral-400 mt-4 text-base sm:text-lg max-w-xl mx-auto font-normal"
          >
            From an idea to impact, every milestone shapes our future.
          </motion.p>

        </div>

        {/* Desktop Cinematic Timeline (Hidden on small mobile screens) */}
        <div className="hidden lg:block relative min-h-[580px] my-10">
          {/* Main Horizontal Timeline Axis Line */}
          <div className="absolute left-4 right-4 top-1/2 -translate-y-1/2 z-0">
            <div ref={trackRef} className="relative w-full h-[3px] flex items-center">
              {/* Start Circle */}
              <div className="absolute -left-3 w-3.5 h-3.5 rounded-full border-2 border-white/60 bg-[#07080a] z-10" />

              {/* WHITE BASE LINE + ORANGE FILL sweeping left→right on scroll */}
              <div ref={lineRef} className="relative w-full h-[2.5px] rounded-full overflow-hidden">
                {/* White base */}
                <div className="absolute inset-0 rounded-full bg-white/20" />
                {/* Orange fill — scaleX from 0 to 1, origin left */}
                <motion.div
                  className="absolute inset-0 rounded-full origin-left"
                  style={{ background: "#FF5A1F", boxShadow: "0 0 10px 2px rgba(255,90,31,0.5)" }}
                  initial={{ scaleX: 0 }}
                  animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                  transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>

              {/* End Arrow */}
              <div className="absolute -right-2 text-white/90 z-20 translate-x-1">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>

              {/* Dots — start white, animate to orange as fill passes through them */}
              {MILESTONES.map((m, i) => (
                <div
                  key={i}
                  style={{ left: `${POSITIONS[i]}%` }}
                  className="absolute -translate-x-1/2 flex items-center justify-center z-20"
                >
                  <motion.div
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    initial={{ backgroundColor: "rgba(255,255,255,0.3)", boxShadow: "none" }}
                    animate={
                      isInView
                        ? {
                            backgroundColor: "#FF5A1F",
                            boxShadow: "0 0 14px 4px rgba(255,90,31,0.55)",
                          }
                        : { backgroundColor: "rgba(255,255,255,0.3)", boxShadow: "none" }
                    }
                    transition={{ duration: 0.35, delay: DOT_DELAYS[i], ease: "easeOut" }}
                  >
                    <div className="w-2.5 h-2.5 rounded-full bg-[#07080a]" />
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          {/* Milestone Cards (Alternating Top / Bottom) */}
          {MILESTONES.map((m, i) => {
            const above = i % 2 === 0;
            const Icon = m.icon;

            return (
              <div
                key={i}
                style={{ left: `${POSITIONS[i]}%` }}
                className={`absolute -translate-x-1/2 z-20 ${
                  above
                    ? "bottom-[calc(50%+28px)]"
                    : "top-[calc(50%+28px)]"
                }`}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                {/* Vertical Connector Line */}
                <div
                  className={`absolute left-1/2 -translate-x-1/2 w-[1.5px] ${
                    above ? "-bottom-7 h-7" : "-top-7 h-7"
                  } bg-gradient-to-b from-[#FF5A1F] to-[#FF5A1F]/30`}
                />

                {/* Card Container */}
                <motion.div
                  initial={{ opacity: 0, y: above ? -30 : 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  data-testid={`tl-card-${i}`}
                  className={`w-[270px] xl:w-[285px] p-5 rounded-2xl border backdrop-blur-xl transition-all duration-300 ${
                    hoveredIdx === i
                      ? "border-[#FF5A1F]/60 bg-[#0d0f14]/90 shadow-[0_10px_30px_rgba(255,90,31,0.2)] -translate-y-1"
                      : "border-white/10 bg-[#0c0e12]/80 shadow-xl shadow-black/50"
                  }`}
                >
                  <div className="flex items-center gap-3.5 mb-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border bg-[#1c1512] border-[#FF5A1F]/30 text-[#FF5A1F]">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-display text-2xl font-bold leading-tight text-[#FF5A1F]">
                        {m.year}
                      </div>
                      <div className="font-heading text-base font-bold text-white leading-snug">
                        {m.title}
                      </div>
                    </div>
                  </div>

                  <p className="text-neutral-400 text-xs sm:text-[13px] leading-relaxed font-normal">
                    {m.body}
                  </p>
                </motion.div>
              </div>
            );
          })}
        </div>

        {/* Mobile / Tablet Vertical Timeline */}
        <div className="lg:hidden relative my-12 pl-6 sm:pl-10">
          {/* Vertical axis line */}
          <div className="absolute left-3 sm:left-5 top-2 bottom-2 w-[3px] rounded-full timeline-line" />

          <div className="space-y-8">
            {MILESTONES.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative pl-6 sm:pl-8"
                >
                  {/* Axis Node Dot */}
                  <div className="absolute -left-[19px] sm:-left-[27px] top-6 w-5 h-5 rounded-full flex items-center justify-center bg-[#FF5A1F] shadow-[0_0_12px_rgba(255,90,31,0.9)]">
                    <div className="w-2 h-2 rounded-full bg-[#07080a]" />
                  </div>

                  {/* Card */}
                  <div className="p-5 sm:p-6 rounded-2xl border border-white/10 bg-[#0c0e12]/85 backdrop-blur-xl shadow-lg">
                    <div className="flex items-center gap-3.5 mb-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border bg-[#1c1512] border-[#FF5A1F]/30 text-[#FF5A1F]">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-display text-2xl font-bold leading-tight text-[#FF5A1F]">
                          {m.year}
                        </div>
                        <div className="font-heading text-lg font-bold text-white">
                          {m.title}
                        </div>
                      </div>
                    </div>
                    <p className="text-neutral-400 text-sm leading-relaxed">
                      {m.body}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Bottom Glassmorphic Pillars Bar */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-16 sm:mt-20 rounded-2xl border border-white/10 bg-[#0a0c10]/90 backdrop-blur-xl p-5 sm:p-7 shadow-2xl"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-4 divide-y sm:divide-y-0 sm:divide-x-0 lg:divide-x divide-white/10">
            {PILLARS.map((p, idx) => {
              const Icon = p.icon;
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-4 ${
                    idx !== 0 ? "pt-4 sm:pt-0 lg:pl-6" : ""
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#1a1412] border border-[#FF5A1F]/30 flex items-center justify-center text-[#FF5A1F] shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading text-sm sm:text-base font-bold text-white">
                      {p.title}
                    </div>
                    <div className="text-neutral-400 text-xs sm:text-sm font-normal mt-0.5">
                      {p.desc}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Timeline;

